/** Acceptance context only: never include invite credentials or the full invite URL. */
export function packAcceptBiFields(packSlug: string | undefined, outcome: string): Record<string, string> {
  const valid = packSlug !== undefined && /^[a-z0-9][a-z0-9-]{0,63}$/.test(packSlug) && !/[^a-z0-9-]/.test(packSlug);
  return { outcome, ...(valid ? { packSlug } : {}) };
}

/**
 * A campaign token only: the value reaches a BI column, and anyone can put any query on a link. The
 * character rule is what keeps it safe; the length only bounds it. 128, not 64: the marketing email's
 * sender (Braze) writes campaign names like `em_blast_promotional_symphony-personalized-email-wix-
 * non-symphony-new_en` (72), and at 64 every email accept arrived with no campaign at all.
 *
 * Wider than the apps' `PackAttribution` (64) on purpose: email links land on this web page (the apps
 * do not claim `symphony.wix.com`), and `pack-open-link.ts` forwards path segments only, so these long
 * query values never reach an app.
 */
const ATTRIBUTION_TOKEN = /^[A-Za-z0-9_.-]{1,128}$/;
/**
 * Query tags read off an invite link. `utm_canvas` is the marketing email sender's (Braze) tag for the
 * canvas a campaign belongs to. It is a QUERY tag only: it arrives on clicked email links, never on a QR
 * path, so the positional path contract below stays at four.
 */
const ATTRIBUTION_PARAMS = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_canvas: 'utmCanvas',
  utm_term: 'utmTerm',
} as const;

/** The four tags that may also ride as path segments after the token, in their positional order. */
const PATH_ATTRIBUTION_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

/**
 * A PATH segment keeps the old 64 limit. Path segments are what `/pack-open` (`pack-open-link.ts`) forwards
 * to the apps, and it and both native `PackAttribution`s cut at 64; a wider rule here would report a
 * campaign on a direct open that the same link loses through the mobile hand-off. Only QUERY tags, which
 * only ever reach this page, get the wider {@link ATTRIBUTION_TOKEN}.
 */
const PATH_ATTRIBUTION_TOKEN = /^[A-Za-z0-9_.-]{1,64}$/;

/**
 * Where the invite was handed out, from the link's UTM tags (Wix site packs tag a marketing email and
 * the Business Manager page differently; on a BM link, `utm_content` is how the merchant reached that page). Deliberately not `ref`: that is the person-referral code
 * `referral-capture.ts` owns. A tag that is missing, repeated or not a plain token is left out.
 */
export function packAcceptAttribution(query: Record<string, string | string[] | undefined>): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const [param, field] of Object.entries(ATTRIBUTION_PARAMS)) {
    const value = query[param];
    if (typeof value === 'string' && ATTRIBUTION_TOKEN.test(value)) fields[field] = value;
  }
  return fields;
}

/**
 * The same tags carried as PATH segments after the invite triple, in the positional contract
 * `pack/<msid>/<slug>/<token>[/<utm_source>[/<utm_medium>[/<utm_campaign>[/<utm_content>]]]]`.
 *
 * The Wix Business Manager QR uses that form because the links service builds the store redirect's
 * `deeplink=` from the path alone, so a query tag never reaches a phone that had to install first; the
 * same `deepLink` then arrives here inside `d`. Positional: a segment that is not a plain token loses its
 * own field only, and the ones after it keep their place. Mirrors iOS `DeepLink.jotActionTarget` and
 * Android `RouteResolver.jotActionForSegments`.
 */
export function packAcceptAttributionFromDeepLink(deepLink: string | string[] | undefined): Record<string, string> {
  const value = Array.isArray(deepLink) ? deepLink[0] : deepLink;
  const segments = (value ?? '').split(/[?#]/)[0].split('/').filter(Boolean);
  const packIdx = segments.indexOf('pack');
  if (packIdx === -1) return {};
  const asQuery: Record<string, string> = {};
  PATH_ATTRIBUTION_PARAMS.forEach((param, offset) => {
    const segment = segments[packIdx + 4 + offset];
    if (segment !== undefined && PATH_ATTRIBUTION_TOKEN.test(segment)) asQuery[param] = segment;
  });
  return packAcceptAttribution(asQuery);
}

/** Path segments and query tags together; a query tag wins when both name the same field. */
export function packAcceptAttributionFor(query: Record<string, string | string[] | undefined>): Record<string, string> {
  return { ...packAcceptAttributionFromDeepLink(query.deepLink), ...packAcceptAttribution(query) };
}
